import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { convertQueryParamsForPostRequest } from '@/api/utils';
import { BulkResponseModel } from '@/features/batch/_models/batch';
import { invalidateBatchListMap } from '@/features/batch/_utils/query-keys';
import type { accountingType } from '@/features/master-table/models';
import { queryKeyMap as projectsSearchQueryKeyMap } from '@/features/project/_utils/query-keys';
import { useInvalidateQuery, useRemoveQueries } from '@/hooks';
import { OptionsModel, stringToBoolean } from '@/utils';
import type { fundSheetTypeEnum, sheetTypeEnum } from '@/utils/enum';
import { z } from 'zod';
import { PriorProjectIdModel } from '../_models/project';
import { convertFormSchemaToRequestProjectMode } from '../_utils/format-project-data';
import { parseParams } from '../_utils/params-formatter';
import { invalidateListMap, queryKeyMap } from '../_utils/query-keys';
import {
  CabinetDecisionWithDefinedIndicatorsModel,
  type ContinueProjectSchema,
  CreateProjectResponseModel,
  DiscriminatedInspectionEvaluationFormSchema,
  DiscriminatedInspectionEvaluationModel,
  type FundInvestmentsFormSchema,
  FundInvestmentsModel,
  GroupSummaryModel,
  type InspectionEvaluationFormSchema,
  type KsAdditionalAttributesExecutionDivergencesFormSchema,
  KsAdditionalAttributesFormSchema,
  ProjectModel,
  ProjectOtherNotesFormSchema,
  ProjectSummaryModel,
  UpdateProjectResponseSchema,
} from '../projects/_models/project';
import { NestedPoliciesModel } from '../projects/_models/project-filter';
import { ProjectsListModel } from '../projects/_models/projects';
import { ProjectFormSchema } from '../projects/forms/_model/project-form';

export type RequestProjectsParams = {
  accountingType: z.infer<typeof accountingType> | null;
  // BEの実装に合わせるためstringが正しい
  accountingTypeIds: string | null;
  cascader: string | null;
  corporateNumber: string | null;
  // BEの実装に合わせるためstringが正しい。projectCategoryを「,」区切りで渡す
  categories: string | null;
  endYear: string | null;
  fiscalYear: string | null;
  fundSheetType: z.infer<typeof fundSheetTypeEnum> | null;
  // BEの実装に合わせるためstringが正しい。implementationMethodを「,」区切りで渡す
  implementationMethods: string | null;
  // BEの実装に合わせるためstringが正しい。reviewTypeを「,」区切りで渡す
  inspectionByExternalExpertReviewType: string | null;
  isCandidateOfInspectionByExternalExpert: boolean | null;
  isPublished: boolean | null;
  lastImplementedFiscalYearRangeMax: number | null;
  lastImplementedFiscalYearRangeMin: number | null;
  page: string | null;
  pageSize: string | null;
  startYear: string | null;
  projectNumbers: string | null;
  skipAdministrativeReformTeamEvaluation: boolean | null;
  q: string | null;
  // BEの実装に合わせるためstringが正しい。baseTextSearchCriteriaを「,」区切りで渡す
  textSearchCriteria: string | null;
  ordering: string | null;
  sheetType: z.infer<typeof sheetTypeEnum> | null;
  // これが何に使われているのか不明
  policy?: string | null;
  // 以下PS用。カンマ区切りの文字列
  organizationIds: string | null;
  policyIds: string | null;
  otherResponsibleOrganizationIds: string | null;
  expenditureBudgetPurposeIds: string | null;
  // 複数数式条件のためのJSON文字列パラメータ
  formulaConditions: string | null;
  // 効果発現経路（指標）検索
  indicatorKeyword: string | null;
  indicatorKeywordSearchOnlyTitleAndDescription: boolean | null;
  indicatorType: string | null;
  indicatorTerm: string | null;
  indicatorDataNatureType: string | null;
  hasOnlyQualitativeIndicator: boolean | null;
  // 公開後に修正された可能性がある事業を紐づけるフラグ。
  potentiallyUnpublished: boolean | null;
};

export const useProjects = <U = z.infer<typeof ProjectsListModel>>({
  params,
  select,
  enabled = true,
}: {
  params: RequestProjectsParams;
  select?: (data: z.infer<typeof ProjectsListModel>) => U;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: queryKeyMap.projects(params),
    queryFn: async () => {
      const projects = await axiosGet('/api/projects/', params);
      return ProjectsListModel.parse(projects);
    },
    select,
    enabled,
  });
};

export const useProjectSummary = ({
  params,
  enabled = true,
}: {
  params: RequestProjectsParams;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: queryKeyMap.projectSummary(params),
    queryFn: async () => {
      const projectSummary = await axiosGet('/api/projects/summary/', params);
      return ProjectSummaryModel.parse(projectSummary);
    },
    enabled,
  });
};

export const useProjectOptions = ({
  params,
  enabled = true,
  select,
}: {
  params?: {
    fiscalYear?: string;
    sheetType?: z.infer<typeof sheetTypeEnum>;
    skipAdministrativeReformTeamEvaluation?: boolean;
    onlyEditable?: boolean;
  };
  enabled?: boolean;
  select?: (data: z.infer<typeof OptionsModel>) => z.infer<typeof OptionsModel>;
}) => {
  return useQuery({
    queryKey: queryKeyMap.projectsOptions(params),
    queryFn: async () => {
      const projects = await axiosGet('/api/projects/options/', params);
      return OptionsModel.parse(projects);
    },
    enabled,
    select,
  });
};

export const useProjectGroupSummary = ({
  id,
  enabled = true,
}: {
  id: string | undefined;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: queryKeyMap.projectGroupSummary(id),
    queryFn: async () => {
      const groupSummary = await axiosGet(
        `/api/projects/groups/${id}/summary/`,
      );
      return GroupSummaryModel.parse(groupSummary);
    },
    enabled: enabled && !!id,
  });
};

export const useProject = (id: string, enabled = true) => {
  return useQuery({
    queryKey: queryKeyMap.project(id),
    queryFn: async () => {
      const project = await axiosGet(`/api/projects/${id}/`);

      const parsed = ProjectModel.parse(project);
      const parsedInspectionEvaluation =
        DiscriminatedInspectionEvaluationModel.safeParse(project);

      return {
        ...parsed,
        historySavedAt: parsed.isPublished ? parsed.publishedAt : undefined,
        inspectionEvaluation: parsedInspectionEvaluation.success
          ? parsedInspectionEvaluation.data.inspectionEvaluation
          : {},
      };
    },
    enabled: enabled && !!id,
  });
};

export const useCreateProject = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (values: z.infer<typeof ProjectFormSchema>) => {
      const { data, sheetType } = ProjectFormSchema.parse(values);

      const {
        organizationIds,
        nestedOtherResponsibleOrganizationIds,
        fiscalYearOfSnapshot,
        ...rest
      } = data;
      const parsedAdditional = ProjectModel.pick({
        fiscalYear: true,
        startYear: true,
        endYear: true,
      }).parse({
        fiscalYear: data.fiscalYear.year(),
        startYear: data.period?.[0]?.year() ?? null,
        endYear: data.period?.[1]?.year() ?? null,
      });

      const otherResponsibleOrganizationIds = [
        ...new Set(
          data.nestedOtherResponsibleOrganizationIds?.flatMap((ids) =>
            ids.length > 0 ? [ids.at(-1)] : [],
          ) ?? [],
        ),
      ];

      const sharedPayload = {
        snapshotId: fiscalYearOfSnapshot?.at(-1),
      };

      const payload =
        sheetType === 'RS'
          ? {
              sheetType,
              ...rest,
              ...parsedAdditional,
              ...sharedPayload,
              organizationId: organizationIds.at(-1),
              relatedOrganizationIds:
                data.nestedRelatedOrganizationIds?.flatMap(
                  (relatedOrganizationIds) =>
                    relatedOrganizationIds.length > 0
                      ? [relatedOrganizationIds.at(-1)]
                      : [],
                ) ?? [],
              ...(data.policiesForProject && {
                policiesForProject: data.policiesForProject.map(
                  (policyForProject) => ({
                    ...policyForProject,
                    policyId: policyForProject.policyId.at(-1),
                  }),
                ),
              }),
              otherResponsibleOrganizationIds,
              ...(data.skipAdministrativeReformTeamEvaluation && {
                legalBases: [],
                majorExpenditureBudgetPurposeId:
                  data.majorExpenditureBudgetPurposeIds?.at(-1) ?? null,
              }),
              inspectionEvaluation: {},
            }
          : sheetType === 'SS'
            ? {
                sheetType,
                ...rest,
                ...parsedAdditional,
                ...sharedPayload,
                organizationId: organizationIds.at(-1),
                relatedOrganizationIds:
                  data.nestedRelatedOrganizationIds?.flatMap(
                    (relatedOrganizationIds) =>
                      relatedOrganizationIds.length > 0
                        ? [relatedOrganizationIds.at(-1)]
                        : [],
                  ) ?? [],
                otherResponsibleOrganizationIds,
                independentAdministrativeAgencyId:
                  data.independentAdministrativeAgencyIds.at(-1),
                inspectionEvaluation: {},
              }
            : {
                sheetType,
                ...rest,
                ...parsedAdditional,
                ...sharedPayload,
                organizationId: organizationIds.at(-1),
                relatedOrganizationIds:
                  data.nestedRelatedOrganizationIds.flatMap(
                    (relatedOrganizationIds) =>
                      relatedOrganizationIds.length > 0
                        ? [relatedOrganizationIds.at(-1)]
                        : [],
                  ),
                otherResponsibleOrganizationIds,
                ...(data.fundAdditionalAttributes && {
                  fundAdditionalAttributes: {
                    ...data.fundAdditionalAttributes,
                    additionalJurisdictionalUsers:
                      data.fundAdditionalAttributes.additionalJurisdictionalUsers?.flatMap(
                        (value) =>
                          value.organizationIds?.length > 0
                            ? [
                                {
                                  ministryId: value.ministryId,
                                  organizationId: value.organizationIds.at(-1),
                                  peopleInChargeIds: value.peopleInChargeIds,
                                  businessManagerIds: value.businessManagerIds,
                                },
                              ]
                            : [],
                      ) ?? [],
                    scheduledEnd: {
                      ...data.fundAdditionalAttributes.scheduledEnd,
                      endDate:
                        data.fundAdditionalAttributes.scheduledEnd?.endDate?.format(
                          'YYYY-MM-DD',
                        ),
                      endDateOfNewApplicationReceiving:
                        data.fundAdditionalAttributes.scheduledEnd?.endDateOfNewApplicationReceiving?.format(
                          'YYYY-MM-DD',
                        ),
                    },
                  },
                }),
                inspectionEvaluation:
                  data.fundAdditionalAttributes?.fundSheetType === 'standard'
                    ? {
                        eligibilityInspection: {
                          methodAndReasonsForSelection:
                            data.inspectionEvaluation?.eligibilityInspection
                              ?.methodAndReasonsForSelection,
                          results:
                            data.inspectionEvaluation?.eligibilityInspection
                              ?.results,
                        },
                        implementationStatusOfInspection:
                          data.inspectionEvaluation
                            ?.implementationStatusOfInspection,
                      }
                    : {},
              };

      const res = await axiosPost('/api/projects/', payload);
      await Promise.all(
        invalidateListMap.updateList().map(async (invalidateList) => {
          await invalidate(invalidateList);
        }),
      );
      return CreateProjectResponseModel.parse(res);
    },
  });
};

export const useUpdateProject = () => {
  const { invalidate } = useInvalidateQuery();

  return useMutation({
    mutationFn: async (
      values: z.infer<typeof ProjectFormSchema> & { id: string },
    ) => {
      const payload = convertFormSchemaToRequestProjectMode({
        values,
        type: 'update',
      });

      const response = await axiosPatch(`/api/projects/${values.id}/`, payload);
      await Promise.all(
        invalidateListMap
          .updateDetail(values.id)
          .map(async (invalidateList) => {
            await invalidate(invalidateList);
          }),
      );
      return UpdateProjectResponseSchema.parse(response);
    },
  });
};

export const useUpdateProjectCabinetDecisions = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof CabinetDecisionWithDefinedIndicatorsModel> & {
        id: string;
      },
    ) => {
      const { id, ...parsed } =
        CabinetDecisionWithDefinedIndicatorsModel.extend({
          id: z.string().uuid(),
        }).parse(values);
      const payload = {
        cabinetDecisionWithDefinedIndicators: parsed,
      };
      const res = await axiosPatch(`/api/projects/${id}/`, payload);
      await invalidate(queryKeyMap.project(id));
      return res;
    },
  });
};

export const useUpdateFundAdditionalAttributes = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof KsAdditionalAttributesFormSchema> & {
        id: string;
      },
    ) => {
      const {
        id,
        compositionBackgrounds: _compositionBackgrounds,
        nationalTreasuryReturnBackgrounds:
          nationalTreasuryReturnBackgroundsForm,
        ...parsed
      } = KsAdditionalAttributesFormSchema.and(
        z.object({ id: z.string().uuid() }),
      ).parse(values);
      const compositionBackgrounds = _compositionBackgrounds.map(
        (background) => ({
          ...background,
          fiscalYearOfBudgetaryProvision:
            background.fiscalYearOfBudgetaryProvision?.year(),
          fiscalYearOfCreation: background.fiscalYearOfCreation?.year(),
          expenditureBudgetPurposeIds:
            background.expenditureBudgetPurposeIds?.flatMap(
              (expenditurePurposeIds) => {
                const lastExpendedPurposeId = expenditurePurposeIds.at(-1);
                return lastExpendedPurposeId ? [lastExpendedPurposeId] : [];
              },
            ),
          haveAppliedLawOfAppropriatenessOfSubsidies: stringToBoolean(
            background.haveAppliedLawOfAppropriatenessOfSubsidies,
          ),
          relatedReviewSheetYear: background.relatedReviewSheetYear?.year(),
        }),
      );
      const nationalTreasuryReturnBackgrounds =
        nationalTreasuryReturnBackgroundsForm.map((nationalTreasury) => ({
          ...nationalTreasury,
          fiscalYear: nationalTreasury.fiscalYear?.year(),
        }));
      const { necessityReasons, necessityType, ...rest } = parsed;
      const payload = {
        fundAdditionalAttributes: {
          ...rest,
          compositionBackgrounds,
          nationalTreasuryReturnBackgrounds,
          scheduledEnd: {
            ...rest.scheduledEnd,
            endDate: rest.scheduledEnd.endDate?.format('YYYY-MM-DD'),
            endDateOfNewApplicationReceiving:
              rest.scheduledEnd.endDateOfNewApplicationReceiving?.format(
                'YYYY-MM-DD',
              ),
          },
        },
      };
      const res = await axiosPatch(`/api/projects/${id}/`, payload);
      await invalidate(queryKeyMap.project(id));
      return res;
    },
  });
};

export const useUpdateFundAdditionalAttributesExecutionDivergences = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({
      id,
      executionDivergences,
    }: z.infer<typeof KsAdditionalAttributesExecutionDivergencesFormSchema> & {
      id: string;
    }) => {
      const payload = {
        fundAdditionalAttributes: {
          executionDivergences,
        },
      };
      const res = await axiosPatch(`/api/projects/${id}/`, payload);
      await invalidate(queryKeyMap.project(id));
      return res;
    },
  });
};

export const useUpdateProjectOtherNotes = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof ProjectOtherNotesFormSchema> & { id: string },
    ) => {
      const { id, ...payload } = ProjectOtherNotesFormSchema.and(
        z.object({ id: z.string().uuid() }),
      ).parse(values);

      const response = await axiosPatch(`/api/projects/${id}/`, payload);
      await invalidate(queryKeyMap.project(id));
      return response;
    },
  });
};

export const useUpdateFundInvestments = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof FundInvestmentsFormSchema> & {
        id: string;
      },
    ) => {
      const { id, ...parsed } = FundInvestmentsModel.extend({
        id: z.string().uuid(),
      }).parse(values);
      const payload = {
        fundAdditionalAttributes: {
          investments: {
            ...parsed,
            accumulatedInvestmentsFromTheGovernment:
              parsed.accumulatedInvestmentsFromTheGovernment.map(
                (accumulatedInvestment) => accumulatedInvestment,
              ),
          } satisfies z.infer<typeof FundInvestmentsModel>,
        },
      };
      const res = await axiosPatch(`/api/projects/${id}/`, payload);
      await invalidate(queryKeyMap.project(id));
      return res;
    },
  });
};

export const useUpdateInspectionEvaluation = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof InspectionEvaluationFormSchema> & {
        sheetType: 'RS' | 'SS' | 'KS';
        id: string;
      },
    ) => {
      const { id, ...parsed } = DiscriminatedInspectionEvaluationFormSchema.and(
        z.object({ id: z.string().uuid() }),
      ).parse(values);
      const { sheetType, ...payload } = parsed;

      const reflectionStatusBasedOnOpinion =
        parsed.sheetType === 'RS'
          ? parsed.reflectionStatusBasedOnOpinion
          : null;

      const res = await axiosPatch(`/api/projects/${id}/`, {
        inspectionEvaluation: {
          ...payload,
          inspectionAndImprovementByAdministrativeJurisdiction: {
            ...parsed.inspectionAndImprovementByAdministrativeJurisdiction,
          },
          ...(sheetType === 'KS' && {
            lowUsedFundInvestigation: {
              ...parsed.lowUsedFundInvestigation,
              hasCeasedOperations: stringToBoolean(
                parsed.lowUsedFundInvestigation.hasCeasedOperations,
              ),
              hasNoResultRecent: stringToBoolean(
                parsed.lowUsedFundInvestigation.hasNoResultRecent,
              ),
              hasFundThatLostPurpose: stringToBoolean(
                parsed.lowUsedFundInvestigation.hasFundThatLostPurpose,
              ),
              hasExceedOneSignificantly: stringToBoolean(
                parsed.lowUsedFundInvestigation.hasExceedOneSignificantly,
              ),
              hasDeemedUnlikelyToBeUsed: stringToBoolean(
                parsed.lowUsedFundInvestigation.hasDeemedUnlikelyToBeUsed,
              ),
            },
          }),
          ...(sheetType === 'RS' && {
            inspectionByExternalExpert: {
              ...parsed.inspectionByExternalExpert,
              lastImplementedFiscalYear:
                parsed.inspectionByExternalExpert.lastImplementedFiscalYear?.year(),
            },
            summariesAndResponses: parsed.summariesAndResponses.map(
              ({ fiscalYear, ...summary }) => ({
                ...summary,
                fiscalYear: fiscalYear?.year(),
              }),
            ),
            otherIndicatedItemsAndResponses:
              parsed.otherIndicatedItemsAndResponses.map(
                ({ fiscalYear, ...indicatedItem }) => ({
                  ...indicatedItem,
                  fiscalYear: fiscalYear?.year(),
                }),
              ),
          }),
          ...(reflectionStatusBasedOnOpinion && {
            reflectionStatusBasedOnOpinion: {
              ...reflectionStatusBasedOnOpinion,
              reflectedAmountOfGeneralAccount:
                reflectionStatusBasedOnOpinion.action === 'abolition' ||
                reflectionStatusBasedOnOpinion.action === 'reduction'
                  ? reflectionStatusBasedOnOpinion.reflectedAmountOfGeneralAccount
                  : null,
              specialAccount:
                reflectionStatusBasedOnOpinion.action === 'abolition' ||
                reflectionStatusBasedOnOpinion.action === 'reduction'
                  ? reflectionStatusBasedOnOpinion.specialAccount
                  : [],
            },
          }),
        },
      });
      await Promise.all([
        invalidate(queryKeyMap.project(id)),
        invalidate(queryKeyMap.projects()),
      ]);
      return res;
    },
  });
};

const ProjectContinueParamsSchema = ProjectModel.pick({
  fiscalYear: true,
}).extend({
  id: z.string().uuid(),
});

export const useContinueProject = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof ContinueProjectSchema> & { id: string },
    ) => {
      const { id, ...payload } = ProjectContinueParamsSchema.parse({
        ...values,
        fiscalYear: values.fiscalYear.year(),
      });
      const res = await axiosPost(`/api/projects/${id}/continue/`, payload);
      await Promise.all([
        ...invalidateListMap.updateList().map(async (invalidateList) => {
          await invalidate(invalidateList);
        }),
        invalidate(queryKeyMap.project(id)),
      ]);
      return res;
    },
  });
};

export const useSplitProject = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof ProjectFormSchema> & { id: string },
    ) => {
      const payload = convertFormSchemaToRequestProjectMode({
        values,
        type: 'split',
      });
      const res = await axiosPost(`/api/projects/${values.id}/split/`, payload);
      await Promise.all([
        ...invalidateListMap.updateList().map(async (invalidateList) => {
          await invalidate(invalidateList);
        }),
        invalidate(queryKeyMap.project(values.id)),
      ]);
      return res;
    },
  });
};

export const useLockProject = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const res = await axiosPost(`/api/projects/${id}/lock/`, {});
      await Promise.all([
        invalidate(queryKeyMap.projects()),
        invalidate(queryKeyMap.groupProjects()),
        invalidate(queryKeyMap.project(id)),
      ]);
      return res;
    },
  });
};

export const useUnlockProject = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const res = await axiosPost(`/api/projects/${id}/unlock/`, {});
      await Promise.all([
        invalidate(queryKeyMap.projects()),
        invalidate(queryKeyMap.groupProjects()),
        invalidate(queryKeyMap.project(id)),
      ]);
      return res;
    },
  });
};

export const useDeleteProject = () => {
  const { invalidate } = useInvalidateQuery();
  const { removeQuery } = useRemoveQueries();
  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const res = await axiosDelete(`/api/projects/${id}/`);
      await Promise.all([
        ...invalidateListMap.updateList().map(async (invalidateList) => {
          await invalidate(invalidateList);
        }),
        invalidate(queryKeyMap.projectGroupSummary()),
        invalidate(queryKeyMap.groupProjects()),
      ]);
      removeQuery(queryKeyMap.project(id));
      removeQuery(queryKeyMap.subProjects(id));
      removeQuery(queryKeyMap.consecutiveFiscalYears(id));
      removeQuery(queryKeyMap.consecutiveRequestAmounts(id));
      removeQuery(queryKeyMap.consecutiveBudgets(id));
      removeQuery(queryKeyMap.consecutiveExecutions(id));
      removeQuery(queryKeyMap.relatedMaterials(id));
      removeQuery(queryKeyMap.activities(id));
      removeQuery(queryKeyMap.activityTree(id));
      removeQuery(queryKeyMap.fundBusiness(id));
      removeQuery(queryKeyMap.businessForm(id));
      removeQuery(queryKeyMap.paymentGroups(id));
      removeQuery(queryKeyMap.payments());
      removeQuery(queryKeyMap.contractorPayments(id));
      removeQuery(queryKeyMap.paymentEdges(id));
      removeQuery(queryKeyMap.paymentGraphData(id));
      return res;
    },
  });
};

export const useGenerateProjectsCsv = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (params: {
      query: { page: string; searchQuery: RequestProjectsParams };
    }) => {
      const query = convertQueryParamsForPostRequest({
        ...params.query.searchQuery,
        page: params.query.page ?? params.query.searchQuery.page,
      });

      const baseUrl = '/api/projects/csv/';
      const res = await axiosPost(
        query ? `${baseUrl}?${query}` : `${baseUrl}`,
        {},
      );

      return BulkResponseModel.parse(res);
    },
    onSuccess: async () => {
      await Promise.all(
        invalidateBatchListMap.batchList().map(async (invalidateList) => {
          await invalidate(invalidateList);
        }),
      );
    },
  });
};

export const useNestedPolicies = <T extends object>({
  params,
  enabled = true,
}: {
  params: T;
  enabled?: boolean;
}) => {
  const parsedParams = parseParams(params);
  return useQuery({
    queryKey: ['nestedPolicies', params],
    queryFn: async () => {
      const nestedPolicies = await axiosGet(
        '/api/projects/nested-policies/',
        parsedParams,
      );
      return NestedPoliciesModel.parse(nestedPolicies);
    },
    enabled,
  });
};

export const usePriorIdFromLineage = ({
  params,
  enabled = true,
}: {
  params: { lineageId?: string; fiscalYear?: number };
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: projectsSearchQueryKeyMap.priorProjectId(params),
    queryFn: async () => {
      const id = await axiosGet('/api/projects/prior-id-from-lineage/', params);
      return PriorProjectIdModel.parse(id);
    },
    enabled: !!(enabled && params.lineageId && params.fiscalYear),
  });
};
